import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import axios from 'redaxios'
import { config, Font, MODALS } from '../../constants'
import { Locale } from '../../modules/locale'
import { ConsoleError } from '../../modules/logging'
import MainModule from '../../modules/main'
import { Routing } from '../../modules/routing'
import { EMAIL_REGEX, escapeHtml, unescapeHtml } from '../../modules/utils'
import { TurnstileChallenge } from '../../utils/cloudflare/TurnstileChallenge'

Locale.getInstance().registerKeySet('fr', 'newsletter', {
  b2cNewsletterTitle: 'On reste en contact ?',
  b2cNewsletterSubtitle:
    'Abonnez-vous à notre infolettre pour rester à l’affût de tout ce qui anime la scène culturelle du moment.',
  b2bNewsletterTitle: 'On reste en contact !',
  b2bNewsletterSubtitle:
    'Abonnez-vous à notre infolettre Pro, dédiée aux professionnels du secteur culturel !',
  emailPlaceholder: 'Courriel',
  subscribe: "S'abonner",
  successToast: 'Merci!',
  successToastAlreadySubscribed: "Vous êtes déjà inscrit à l'infolettre de La Vitrine!",
  failureToast: 'Une erreur est survenue',
  invalidEmail: 'Courriel invalide',
})

Locale.getInstance().registerKeySet('en', 'newsletter', {
  b2cNewsletterTitle: 'Let’s stay in touch',
  b2cNewsletterSubtitle:
    'Subscribe to our newsletter to stay on top of what’s happening on the cultural scene in Quebec.',
  b2bNewsletterTitle: 'Stay in the know!',
  b2bNewsletterSubtitle:
    'Subscribe to our Pro Zone Newsletter, dedicated to professionals in the cultural sector!',
  emailPlaceholder: 'E-mail',
  subscribe: 'Sign up',
  successToast: 'Thanks for signing up!',
  successToastAlreadySubscribed: "You are already subscribed to La Vitrine's newsletter!",
  failureToast: 'An error occured',
  invalidEmail: 'Invalid e-mail',
})

export interface LvcNewsletter {}

export class LvcNewsletter extends MinzeElement {
  attrs: Attrs = ['newsletter-type', 'b2c']
  modalId = MODALS.FORGOT_PASSWORD
  reactive: Reactive = [
    ['value', ''],
    ['error', ''],
    ['newsletterType', 'b2c'],
    ['subscribed', false],
    ['loading', false],
  ]
  static observedAttributes = ['newsletter-type']
  isMobile = window.matchMedia('(max-width: 730px)').matches
  private turnstile: TurnstileChallenge | null = null
  private cfTurnstileToken: string | null = null

  async onReady() {
    const mainModule = MainModule.getInstance()
    const authHandler = this.handleAuthChanged.bind(this)
    mainModule.getEE().on('lvc:user_state_changed', authHandler)
    await this.renderBasedOnRoute()
    await this.handleAuthChanged(mainModule.getAuth().isLoggedIn())

    // Initialize the Turnstile challenge
    this.turnstile = new TurnstileChallenge()
  }

  // Turnstile returns the token
  async prepareTurnstileToken(): Promise<string> {
    if (!this.turnstile) {
      throw new Error('Turnstile is not initialized')
    }

    try {
      // Always obtain a fresh token per attempt
      const token = await this.turnstile.initialize()
      this.cfTurnstileToken = token
      return token
    } catch (error) {
      console.error('Error during Turnstile initialization')
      throw error
    }
  }

  // check the route to see if we need to render b2b version or b2c version
  // if the route contains "pro-zone" or "espace-pro" we render the b2b version
  renderBasedOnRoute = async () => {
    const mainModule = MainModule.getInstance()
    const lang = mainModule.getLang()
    const route = mainModule.getRouting().getCurrentPage()
    const proSpaceRoute = Routing.ROUTES[lang].partnersPage
    const resourceCenterRoute = Routing.ROUTES[lang].resourcePage
    if (route.includes(proSpaceRoute) || route.includes(resourceCenterRoute)) {
      this.newsletterType = 'b2b'
    }
  }

  handleAuthChanged = async (data: any): Promise<void> => {
    const mainModule = MainModule.getInstance()
    const profile = await mainModule.getAuth().loadProfile()
    // if the newsletter type is b2b, we always show it
    if (this.newsletterType === 'b2b') {
      return
    }
    // otherwise we check if the user is subscribed to the newsletter
    this.subscribed = profile?.notifications?.newsletter || false
  }

  validateEmail = (email: string) => {
    this.error = ''
    if (!email) {
      this.error = Locale.getKey('signup.errors.email')
    }
    if (email && !EMAIL_REGEX.test(email)) {
      this.error = Locale.getKey('newsletter.invalidEmail')
    }
  }

  submit = async () => {
    this.loading = true // show loading spinner
    const mainModule = MainModule.getInstance()
    const emailField = this.select('#email-input') as HTMLInputElement
    const email = unescapeHtml(emailField?.value)
    this.validateEmail(email)
    let userId = await mainModule.getAuth().getUserAuth()?.userId

    try {
      // Always get a fresh token for each submission
      await this.prepareTurnstileToken()

      // we have 2 different lists, b2b and b2c
      const requestEndpoint = this.newsletterType === 'b2c' ? 'contact_news' : 'contact_news_b2b'

      if (!this.error) {
        this.value = ''
        this.error = ''
        let response = await axios.post(`${config.backendApi}/user/public/${requestEndpoint}`, {
          userId: userId,
          email,
          lang: Locale.getInstance().getLanguage(),
          cfTurnstileToken: this.cfTurnstileToken,
        })

        if (response.status === 200) {
          if (response?.data?.alreadySubscribed) {
            mainModule.showToastAlert(
              Locale.getKey('newsletter.successToastAlreadySubscribed'),
              5000
            )
            // Update the user profile in case the newsletter flag in firebase is not up to date
            mainModule.getAuth().saveProfile('profile-notification', {
              notifications: true,
            })
            this.subscribed = true
          } else {
            mainModule.showToastAlert(Locale.getKey('newsletter.successToast'), 5000)
          }
          try {
            //@ts-ignore
            fbq('track', 'Subscribe', {
              type: `newsletter_${this.newsletterType}`,
            })
          } catch (err) {
            console.log(err)
          }
          try {
            await mainModule.analyticsTrack('inscription_infolettre', {
              emplacement: 'footer',
            })
          } catch (err) {
            console.log('Error tracking newsletter subscription:', err)
          }
          this.subscribed = true
        }
      }
    } catch (e) {
      mainModule.showToastAlert(Locale.getKey('newsletter.failureToast'), 5000)
      ConsoleError(true, e)
    } finally {
      // Clear token after each attempt to avoid reusing single-use tokens
      this.cfTurnstileToken = null
      this.loading = false
      this.rerender(true)
    }
  }

  html = () => `
  ${
    !this.subscribed
      ? /*html*/ `
      
      <lvc-hand-drawn-box variant="box1" bgColor="#1E007D">
        
        <div class="footer_cta-wrapper">
          <lvc-hand-icon></lvc-hand-icon>
      <div class="footer_cta-text-wrapper">
        <div class="footer_cta-text">
          ${Locale.getKey(`newsletter.${this.newsletterType}NewsletterTitle`)}
        </div>
        <p class="footer_cta-small-text">
          ${Locale.getKey(`newsletter.${this.newsletterType}NewsletterSubtitle`)}
        </p>
      </div>
      <div class="footer_cta-form-block w-form">
        <div class="form-wrapper">
          <div class="input-wrapper">
            <lvc-input 
              id="email-input" 
              placeholder="${Locale.getKey('newsletter.emailPlaceholder')}" 
              padding="14px 16px" 
              margintop="0"
              type="email"
              default-value="${escapeHtml(this.value as string)}"
            ></lvc-input>
            <button id="submit-email" class="cta-button" type="button" ${this.loading ? 'disabled' : ''}>
              ${Locale.getKey('newsletter.subscribe')}
              ${this.loading ? `<div class="btn-loader"></div>` : ``}
            </button>
          </div>
              ${this.error ? `<lvc-error error="${this.error}"></lvc-error>` : ''}

        </div>
                 
      </div>
       
    </div>
    </lvc-hand-drawn-box>`
      : ''
  }
    
  `

  css = () => `
    :host {
      ${
        this.subscribed
          ? `
        min-height: 0;
        top: 0;
        padding-top: 129px;
        position: static;
      `
          : ''
      }
    }

    lvc-hand-icon{
      position: absolute;
      left: -9px;
      overflow: hidden;
    }

    .cta-button {
      display:flex;
      align-items: center;
      gap: 4px;
      background-color: #FF4600;
      color: #fff;
      font-family: inherit;
      border-radius: 999px;
      outline: none;
      border: none;
      padding: 8px 16px;
      font-size: 16px;
      font-weight: ${Font.weight.medium};
      cursor: pointer;
    }

    .cta-button:disabled {
      background-color: #ccc;
      cursor: default;
    }

    .btn-loader {
      border: 2px solid #fff;
      border-top: 2px solid transparent;
      border-radius: 50%;
      width: 20px;
      height: 20px;
      animation: spin 1s linear infinite;
    }
    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }

    .input-wrapper {
      display:flex;
      border-radius: 999px;
      background-color: #fff;
      padding: 4px;
      height: fit-content;
      width: 100%;
    }
    
    .footer_cta-wrapper {
      grid-column-gap: 70px;
      grid-row-gap: 65px;
      background-color: transparent;
      border-radius: .75rem;
      align-items: center;
      justify-content: space-between;
      padding: 30px 80px;
      display: flex;
      position: relative;
      ${this.subscribed ? 'display: none;' : ''}
    }
    .footer_cta-text-wrapper {
      width: auto;
      grid-column-gap: 12px;
      grid-row-gap: 12px;
      color: #fff;
      flex-direction: column;
      display: flex;
      margin-left: 8rem;
    }

    .footer_cta-text {
      font-size: 40px;
      font-weight: ${Font.weight.extraBold};
      line-height: 1.2em;
    }
    .footer_cta-small-text {
      font-size: 14px;
      font-weight: ${Font.weight.medium};
    }
    .footer_cta-form-block {
      gap: 16px;
      height: 100%;
      width: 100%;
    }
    @media screen and (max-width: 768px) {
      .footer_cta-wrapper {
        flex-direction: column;
        padding: 24px;
        grid-row-gap: 24px;
      }

      .footer_cta-text-wrapper {
        margin-left: 0;
        margin-top: 8rem;
      }
      .footer_cta-text {
        font-size: 26px;
        line-height: 120%;
      }
      .footer_cta-small-text {
        margin: 0;
        font-size: 12px;
      }
      .footer_cta-form-block {
        max-width: 100%;
        min-width: auto;
        flex-direction: column;
        width: 100%;
      }
      #submit-email {
        width: auto;
      }

      lvc-hand-icon{
        transform: rotate(90deg);
        position: absolute;
        left: 30px;
        top: 17px;
        overflow: hidden;
      }
    }
  `

  eventListeners: EventListeners = [['#submit-email', 'click', this.submit.bind(this)]]
}
